package domain.score

import domain.cards.RunState
import engine.model.events.DefaultGameEvents
import engine.model.events.EventOrchestrator
import engine.model.events.GameEvent
import engine.util.Logger
import game.events.GravitarEvents

/**
 * Awards coins (Gravitar's in-run currency) based on the quality of each clear.
 *
 * Subscribes to [DefaultGameEvents.ScoreUpdated] via the global [EventOrchestrator].
 * When a qualifying [GravitarMoveType] is detected, coins are added to [RunState]
 * and a [GravitarEvents.CoinsAwarded] event is published for the HUD.
 *
 * Call [attach] once when a game session starts, [detach] when it ends.
 */
class StoryCurrencyCalculator(
    private val gameId: String,
    private val runState: RunState,
) {
    private val listener: (GameEvent) -> Unit = { ev ->
        if (ev is DefaultGameEvents.ScoreUpdated && ev.gameId == gameId) {
            Logger.info { "StoryCurrencyCalculator: score updated $ev" }
            val moveType = ev.moveType as? GravitarMoveType
            if (moveType != null) onScoreUpdated(moveType)
        }
    }

    fun attach() {
        EventOrchestrator.listeners.add(listener)
    }

    fun detach() {
        EventOrchestrator.listeners.remove(listener)
    }

    private fun onScoreUpdated(moveType: GravitarMoveType) {
        val base = baseCoins(moveType)
        if (base == 0) return

        val earned = (base * runState.totalCoinMultiplier).toInt().coerceAtLeast(1)
        runState.addCoins(earned)

        EventOrchestrator.publish(
            GravitarEvents.CoinsAwarded(
                amount = earned,
                reason = moveType.displayName,
                gameId = gameId,
            )
        )
    }

    private fun baseCoins(moveType: GravitarMoveType): Int = when (moveType) {
        GravitarMoveType.NONE,
        GravitarMoveType.BLIP,
        GravitarMoveType.BURST,
        GravitarMoveType.FLARE  -> 0

        GravitarMoveType.GRAVITAR  -> 1
        GravitarMoveType.PULL_MINI -> 1
        GravitarMoveType.PULL      -> 2
        GravitarMoveType.PULL_DOUBLE -> 3
        GravitarMoveType.PULL_TRIPLE -> 4

        GravitarMoveType.VOID     -> 5
        GravitarMoveType.COLLAPSE -> 8
        GravitarMoveType.EVENT    -> 12
        GravitarMoveType.HORIZON  -> 20
        GravitarMoveType.OBLIVION -> 30
    }
}
